/**
 * Einheiten umrechnen: Gewicht und Volumen untereinander und – über die
 * Dichte einer Zutat – Volumen in Gewicht und zurück. So lässt sich „2 EL
 * Zucker“ auf der Waage abwiegen oder „200 g Milch“ im Messbecher abmessen.
 */
import { MILLILITERS_PER_TABLESPOON, MILLILITERS_PER_TEASPOON, normalizeUnit } from './units.ts';

/** Gramm je Einheit. */
const MASS: Readonly<Record<string, number>> = { mg: 0.001, g: 1, kg: 1000 };

/** Milliliter je Einheit. */
const VOLUME: Readonly<Record<string, number>> = {
  ml: 1,
  cl: 10,
  dl: 100,
  l: 1000,
  TL: MILLILITERS_PER_TEASPOON,
  EL: MILLILITERS_PER_TABLESPOON,
  Tasse: 250,
};

/**
 * Dichte in Gramm je Milliliter für häufige Zutaten. Lose Schüttgüter wie
 * Mehl sind deutlich leichter als Wasser. Werte sind Küchenmittelwerte.
 * Längere Stichwörter stehen vorn, damit „Puderzucker“ vor „Zucker“ greift.
 */
const DENSITIES: readonly (readonly [string, number])[] = [
  ['puderzucker', 0.56],
  ['brauner zucker', 0.9],
  ['vanillezucker', 0.85],
  ['speisestärke', 0.6],
  ['stärke', 0.6],
  ['backpulver', 0.9],
  ['natron', 1.1],
  ['paniermehl', 0.45],
  ['semmelbrösel', 0.45],
  ['haferflocken', 0.4],
  ['kokosraspel', 0.35],
  ['gemahlene mandeln', 0.45],
  ['mandeln gemahlen', 0.45],
  ['nüsse gemahlen', 0.45],
  ['gemahlene nüsse', 0.45],
  ['kakao', 0.45],
  ['grieß', 0.65],
  ['mehl', 0.55],
  ['zucker', 0.85],
  ['salz', 1.2],
  ['reis', 0.85],
  ['linsen', 0.85],
  ['rosinen', 0.6],
  ['honig', 1.42],
  ['sirup', 1.35],
  ['sojasauce', 1.15],
  ['sojasoße', 1.15],
  ['joghurt', 1.03],
  ['quark', 1.1],
  ['schmand', 1.0],
  ['saure sahne', 1.0],
  ['sahne', 1.0],
  ['milch', 1.03],
  ['buttermilch', 1.03],
  ['öl', 0.92],
  ['butter', 0.95],
  ['essig', 1.0],
  ['wein', 1.0],
  ['brühe', 1.0],
  ['fond', 1.0],
  ['saft', 1.04],
  ['wasser', 1.0],
];

/** Dichte einer Zutat oder null, wenn unbekannt. */
export function densityFor(name: string): number | null {
  const lower = ` ${name.toLowerCase()} `;
  for (const [keyword, density] of DENSITIES) {
    if (lower.includes(keyword)) {
      return density;
    }
  }
  return null;
}

/** Ziel der Umrechnung. */
export type UnitMode = 'original' | 'weight' | 'volume';

/** Bezeichnungen der Umrechnungsziele. */
export const UNIT_MODE_LABELS: Readonly<Record<UnitMode, string>> = {
  original: 'wie im Rezept',
  weight: 'zum Abwiegen',
  volume: 'zum Abmessen',
};

/** Eine Menge mit Einheit. */
export interface Measured {
  readonly value: number;
  readonly unit: string;
}

/** Ist die Einheit ein Gewicht oder ein Volumen? */
export function unitKind(unit: string): 'mass' | 'volume' | 'other' {
  const normalized = normalizeUnit(unit);
  if (MASS[normalized.toLowerCase()] !== undefined) {
    return 'mass';
  }
  if (VOLUME[normalized] !== undefined || VOLUME[normalized.toLowerCase()] !== undefined) {
    return 'volume';
  }
  return 'other';
}

/** Milliliter einer Volumenangabe. */
function toMillilitres(value: number, unit: string): number {
  const normalized = normalizeUnit(unit);
  return value * (VOLUME[normalized] ?? VOLUME[normalized.toLowerCase()] ?? 1);
}

/**
 * Kleine Volumen in Löffeln, größere in Millilitern – so wie man sie in der
 * Küche abmisst.
 */
function volumeForKitchen(millilitres: number): Measured {
  if (millilitres < MILLILITERS_PER_TABLESPOON) {
    return { value: millilitres / MILLILITERS_PER_TEASPOON, unit: 'TL' };
  }
  if (millilitres <= 3 * MILLILITERS_PER_TABLESPOON) {
    return { value: millilitres / MILLILITERS_PER_TABLESPOON, unit: 'EL' };
  }
  return { value: millilitres, unit: 'ml' };
}

/**
 * Rechnet eine Menge in die gewünschte Art um. Liefert null, wenn das nicht
 * geht (zum Beispiel „1 Bund“ oder eine Zutat ohne bekannte Dichte); dann
 * bleibt die Angabe wie im Rezept.
 */
export function convertMeasure(value: number, unit: string, mode: UnitMode, name: string): Measured | null {
  if (mode === 'original') {
    return null;
  }
  const kind = unitKind(unit);
  const density = densityFor(name);
  if (mode === 'weight') {
    if (kind === 'mass') {
      return { value: value * (MASS[normalizeUnit(unit).toLowerCase()] ?? 1), unit: 'g' };
    }
    if (kind === 'volume' && density !== null) {
      return { value: toMillilitres(value, unit) * density, unit: 'g' };
    }
    return null;
  }
  if (kind === 'volume') {
    return volumeForKitchen(toMillilitres(value, unit));
  }
  if (kind === 'mass' && density !== null) {
    const grams = value * (MASS[normalizeUnit(unit).toLowerCase()] ?? 1);
    return volumeForKitchen(grams / density);
  }
  return null;
}
